import { httpUserBalances } from "api/support/user";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import SupportSidebar from "components/layout/SupportSidebar";
import Search from "components/ui/Search";
import TransactionItem from "components/ui/TransactionItem";
import { DateTime } from "luxon";
import { useQuery } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supportRoutes } from "routes/routes";
import currency, {
  cryptoFormat,
  moneyFormat,
  getUserCurrency,
} from "utilities/currency";
import { cryptos } from "utilities/wallets";

const UserBalances: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const userID = params.user as string;

  const {
    data: { user, balances, rate },
  } = useQuery(["userBalances", userID], () => httpUserBalances(userID), {
    initialData: {
      user: {},
      rate: {
        buy: {
          usd: 0,
        },
        sell: {
          usd: 0,
        },
      },
      balances: { naira: 0, totalRecurring: 0 },
    },
    onError: (err) => {
      navigate(-1);
    },
  });

  const fiatCurrency = user.currency || getUserCurrency(user);
  const usdSellRate =
    rate.sell.usdRates?.[fiatCurrency] ??
    (typeof rate.sell.usd === "object"
      ? rate.sell.usd[fiatCurrency]
      : fiatCurrency === "NGN"
        ? rate.sell.usd
        : 0);
  const usdValue = (amount: number) =>
    usdSellRate > 0 ? amount / usdSellRate : 0;

  return (
    <Container>
      <SupportSidebar />
      <Main className="mx-5 mt-5 text-left">
        <div className="flex items-center justify-between w-full">
          <h3 className="text-2xl">@{user?.userName} balances</h3>
          <div className="w-3/6">
            <Search type="user" />
          </div>
        </div>

        <div className="flex gap-8 mt-14">
          <div className="w-7/12">
            <div className="flex flex-wrap w-full gap-5">
              <div className="w-5/12 px-3 py-5 border border-gray-500 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="font-light">{fiatCurrency}</span>
                  <i className="p-1 bg-orange-100 rounded fa fa-bitcoin"></i>
                </div>
                <p className="my-1 text-xl font-medium">
                  {currency(fiatCurrency)} {moneyFormat(balances.naira)}
                </p>
                <p className="text-sm font-light">
                  {currency("usd")} {moneyFormat(usdValue(balances.naira))}
                </p>
              </div>
              {cryptos.map((crypto) => (
                <div
                  key={crypto}
                  className="w-5/12 px-3 py-5 border border-gray-500 rounded-lg"
                >
                  <div className="flex justify-between text-sm">
                    <span className="font-light">{crypto?.toUpperCase()}</span>
                    <i className="p-1 bg-orange-100 rounded fa fa-bitcoin"></i>
                  </div>
                  <p className="my-1 text-xl font-medium">
                    {cryptoFormat(balances[crypto]?.amount)}{" "}
                    {crypto?.toUpperCase()}
                  </p>
                  <p className="text-sm font-light">
                    {currency("usd")}{" "}
                    {moneyFormat(usdValue(balances[crypto]?.naira))}{" "}
                    <span className="ml-5">
                      {currency(fiatCurrency)}{" "}
                      {moneyFormat(balances[crypto]?.naira)}
                    </span>
                  </p>
                </div>
              ))}
              <div className="w-5/12 px-3 py-5 border border-gray-500 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="font-light">Recurring</span>
                  <i className="p-1 bg-orange-100 rounded fa fa-bitcoin"></i>
                </div>
                <p className="my-1 text-xl font-medium">
                  {currency(fiatCurrency)}{" "}
                  {moneyFormat(balances.totalRecurring)}
                </p>
                <p className="text-sm font-light">
                  {currency("usd")}{" "}
                  {moneyFormat(usdValue(balances.totalRecurring))}
                </p>
              </div>
            </div>
          </div>

          <div className="w-5/12 px-8 py-10 rounded-lg bg-orange-50">
            <h4 className="mb-5">@{user?.userName}</h4>

            <TransactionItem title="Username" value={`@${user?.userName}`} />
            <TransactionItem
              title="Registered full Name"
              value={`${user?.firstName} ${user?.lastName}`}
            />
            {/* <TransactionItem title="e-mail" value={user?.email} />
            <TransactionItem title="Phone Number" value={user?.phoneNumber} /> */}
            <TransactionItem
              title="Verification Level"
              value={`Level ${user?.level}`}
            />
            <TransactionItem
              title="Date registered"
              value={DateTime.fromISO(user?.regDate).toFormat("D")}
            />

            <div className="flex flex-wrap mt-10 gap-x-20 gap-y-10">
              <Link to={`${supportRoutes.balances}/user/${user?.id}`}>
                <span
                  className={`px-5 py-1.5 bg-black text-white rounded-full mr-3 cursor-pointer`}
                >
                  Balances
                </span>
              </Link>
              <Link to={`${supportRoutes.transactions}/user/${user.id}`}>
                <span
                  className={`px-5 py-1.5 bg-black text-white rounded-full mr-3 cursor-pointer`}
                >
                  Transactions
                </span>
              </Link>
              <Link to={`${supportRoutes.analytics}/user/${user.id}`}>
                <span
                  className={`px-5 py-1.5 bg-black text-white rounded-full mr-3 cursor-pointer`}
                >
                  Analytics
                </span>
              </Link>
              <Link to={`${supportRoutes.explorer}/user/${user.id}`}>
                <span
                  className={`px-5 py-1.5 bg-black text-white rounded-full mr-3 cursor-pointer`}
                >
                  Addresses Explorer
                </span>
              </Link>
            </div>
          </div>
        </div>
      </Main>
    </Container>
  );
};
export default UserBalances;
